import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector, RootStateOrAny } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation, usePermissions } from "hooks";
import { SettingLayout } from "layouts/SettingLayout";
import {
  useZipcodeInternacionalSearchParams,
  type ZipcodeInternacionalFilters as ZipcodeInternacionalFilterValues,
} from "hooks/store/use-zipcode-internacional-search-params";
import type { InnerNavigatorOption } from "components/ui/InnerNavigator";
import { translations } from "./translations";

import {
  PaginateZipcodeInternacionalActions,
  PaginateZipcodeInternacionalState,
} from "store/ducks/settings/zipcode-internacional";

import { MainContainer } from "components/shared";
import { DataTable2 } from "components/ui/DataTable2";
import { PageHeaderForFilters } from "components/ui/page-header-for-filters";
import { ZipcodeInternacionalFilters } from "./components/ZipcodeInternacionalFilters";
import { useZipcodeInternacionalColumns } from "./hooks/use-zipcode-internacional-columns";

export const ListZipcodeInternacional: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { getTranslation } = useTranslation(translations);
  const { checkUserPermission } = usePermissions();

  const {
    filters,
    paginationControl,
    updateFilters,
    updatePaginationControl,
    clearState,
  } = useZipcodeInternacionalSearchParams();

  const { data, loading, pagination } = useSelector<
    RootStateOrAny,
    PaginateZipcodeInternacionalState
  >((state) => state.paginateZipcodeInternacional);

  const canEdit = checkUserPermission("editZipcodeInternacional");

  const tabOptions = React.useMemo<InnerNavigatorOption[]>(
    () => [
      {
        route: "/settings/zipcode-internacional",
        hasPermission: checkUserPermission("listZipcodeInternacional"),
        title: getTranslation("zipcodeInternacional"),
      },
      {
        route: "/settings/zipcode-internacional/new",
        hasPermission: checkUserPermission("insertZipcodeInternacional"),
        title: getTranslation("novoZipcode"),
      },
    ],
    [checkUserPermission, getTranslation]
  );

  const handleDatatablePagination = useCallback(
    (payload: typeof paginationControl) => {
      updatePaginationControl(payload);
    },
    [updatePaginationControl]
  );

  const handleNavigateToDetails = useCallback(
    (zipcodeId: number) => {
      history.push(`/settings/zipcode-internacional/${zipcodeId}`);
    },
    [history]
  );

  const columns = useZipcodeInternacionalColumns({
    canEdit,
    onClickNavigateToDetails: handleNavigateToDetails,
  });

  const handleFiltersSubmission = useCallback(
    (updatedFilters: ZipcodeInternacionalFilterValues) => {
      updatePaginationControl({ page: 1 });
      updateFilters(updatedFilters);
    },
    [updateFilters, updatePaginationControl]
  );

  const getData = useCallback(() => {
    dispatch(
      PaginateZipcodeInternacionalActions.request({
        ...filters,
        ...paginationControl,
      })
    );
  }, [dispatch, filters, paginationControl]);

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <MainContainer>
      <SettingLayout
        page="zipcode-internacional"
        options={tabOptions}
      >
        <PageHeaderForFilters
          headerTitle={
            <div className="w-full font-GilroySemibold">
              {getTranslation("filtersTitle")}
            </div>
          }
          className="mb-6"
        >
          <ZipcodeInternacionalFilters
            onSubmitFilters={handleFiltersSubmission}
            onResetFilters={clearState}
          />
        </PageHeaderForFilters>
        <DataTable2
          key={`${paginationControl.page}-${paginationControl.limit}`}
          columns={columns}
          data={data}
		  showControlColumns={false}
          manualPagination
          isLoading={loading}
          onPaginationChange={handleDatatablePagination}
        //   onRowClick={(row) => {
        //     if (canEdit) {
        //       handleNavigateToDetails(row.original.id);
        //     }
        //   }}
          pagination={{
            ...pagination,
            page: paginationControl.page,
            perPage: paginationControl.limit,
          }}
        />
      </SettingLayout>
    </MainContainer>
  );
};
